import { useEffect } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { trackError } from '@vend/utilities'
import { fetchLspayCashDiscountSettings } from '../../../../functions/payment-types/fetchLspayCashDiscountSettings'
import { isReturn } from '../../../../functions/sale'
import { useRegisterManager, useSaleManager } from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectSaleLocalId } from '../../../../state/slices/currentSale/currentSaleSlice'

/**
 * Fetches the LSPay cash discount settings for the active outlet on mount
 * and applies the rate to the current sale. This lets the rest of the
 * payment UI (Cash button label, banners, etc.) preview the discount rate
 * before the customer clicks Cash.
 *
 * Re-runs when the sale local id changes so each new sale picks up the
 * outlet rate after `newSale()` resets `lspCashDiscountPercentage` to zero.
 *
 * No-op when there is no active outlet, no LSPay payment type, or when the
 * LSPay endpoint returns a disabled / zero rate.
 */
export const useApplyLspCashDiscount = (
  lspayPaymentType?: ApiRetailerPaymentType
): void => {
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const returnFor = useAppSelector(state => state.currentSale.returnFor)
  // `getActiveOutlet` is optional-chained defensively: many existing
  // payment-panel test mocks don't expose it, and we'd rather no-op the hook
  // than crash those suites.
  const activeOutletId = registerManager.getActiveOutlet?.()?.id

  useEffect(() => {
    let cancelled = false
    // Return sales copy the rate from the original sale in sale-loader; do not
    // overwrite it with the outlet default (which may be disabled or zero).
    if (
      !activeOutletId ||
      !saleLocalId ||
      !lspayPaymentType ||
      isReturn({ returnFor })
    ) {
      return undefined
    }

    fetchLspayCashDiscountSettings({ outletId: activeOutletId })
      .then(settings => {
        if (cancelled) return
        const rate = settings?.enabled && settings.rate > 0 ? settings.rate : 0
        saleManager.setLspCashDiscountPercentage(rate).catch(() => undefined)
      })
      .catch(err => trackError(err as Error))

    return () => {
      cancelled = true
    }
  }, [activeOutletId, saleLocalId, lspayPaymentType, returnFor, saleManager])
}
